import { ListView, TopBarRoute } from "components/templates";
import { useNavigate, useParams } from "react-router";
import { api } from "services/rpc/RpcProvider";
import { WorkflowTimeline, WorkflowTimelineSkeletonLoader } from "../../flows/containers";
import { WorkflowStepEditorDrawer } from "../../flows/containers/WorkflowStepEditor/WorkflowStepEditorDrawer";
import { useSearch } from "foundation";
import styled from "styled-components";
import { useMemo, useState } from "react";
import { Button, Stack } from "@introist/react-foundation/v2";
import { UseTemplateModal } from "../components/UseTemplateModal";

const Wrapper = styled.section`
  display: grid;
  grid-row-gap: 1rem;
  grid-template-rows: auto 1fr;
  min-height: 88vh;
`;

export const TemplateViewerRoute = () => {
  const navigate = useNavigate();
  const { id, stepId } = useParams<{ id: string; stepId?: string }>();
  const [useTemplateModalOpen, setUseTemplateModalOpen] = useState(false);

  const { data: templates } = api.workflows.templates.templates.useQuery({});
  const { data: steps } = api.workflows.templates.steps.list.useQuery(
    { templateId: id! },
    { enabled: !!id }
  );

  const currentTemplate = templates?.find(t => t.id === id);

  const currentStep = useMemo(() => {
    if (!stepId || !steps) return undefined;
    return steps.find(s => s.id === stepId);
  }, [stepId, steps]);

  const {
    filtered: filteredSteps,
    search,
    setSearch
  } = useSearch(steps, { fields: ["stepTitle"] });

  return (
    <TopBarRoute
      paths={[
        { key: "templates", title: "Templates", onClick: () => navigate("/templates") },
        { key: "template", title: currentTemplate?.title || "" }
      ]}
      actions={
        <Stack>
          <Button
            variant="outlined"
            size="small"
            startIcon="playCircle"
            onClick={() => setUseTemplateModalOpen(true)}
          >
            Use this template
          </Button>
        </Stack>
      }
    >
      <Wrapper>
        <ListView.Header
          title="Steps"
          count={filteredSteps?.length}
          search={{ search, setSearch }}
        />

        {(!currentTemplate || !steps) && <WorkflowTimelineSkeletonLoader />}
        {currentTemplate && steps && (
          <WorkflowTimeline
            steps={filteredSteps ?? []}
            workflow={currentTemplate as any}
            onStepClick={stepId => navigate(stepId, { relative: "path" })}
            showAssistant={false}
          />
        )}
      </Wrapper>

      <WorkflowStepEditorDrawer
        open={!!currentStep}
        onClose={() => {
          navigate(`/templates/${id}`, { replace: true });
        }}
        workflowId={id!}
        step={currentStep!}
        mode="template"
      />

      <UseTemplateModal
        open={useTemplateModalOpen}
        onClose={() => setUseTemplateModalOpen(false)}
        templateId={id!}
        templateName={currentTemplate?.title || ""}
        onWorkflowCreated={workflowId => {
          navigate(`/flows/edit/${workflowId}`);
        }}
      />
    </TopBarRoute>
  );
};
